import * as React from "react";

import { Block } from "kanopi-components/Block";
import { Button, CloseButton } from "kanopi-components/Button";
import { Card, CardBody } from "kanopi-components/Card";
import {
  HeadingXSmall,
  ParagraphMedium,
  LabelXSmall,
} from "kanopi-components/typography";
import { useUnderBreakpoint } from "kanopi-components/useUnderBreakpoint";
import { toCamelCase } from "kanopi-components/utils/string";

import { CoverSectionIcon } from "kw/util/CoverSection/CoverSectionIcon";
import { isCoverDisabledBySubCovers } from "kw/util/CoverSection/utils";
import {
  useTrackCoverEditStarted,
  useTrackCoverRemoved,
} from "kw/util/CoverSection/useTracking";
import { CoverSummaryFlow } from "./CoverSummaryFlow";

/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */
// TODO: Enable the proptype check next time when this file is changed

type TProps = {
  coverSection?: TCoverSection;
  riskProfile?: TRiskProfile;
  disable?: Function;
  edit?: Function;
  disabled?: boolean;
  children?: React.ReactNode;
  finishedRoute?: string;
};

// NOTE: Not sure if this naming is appropriate?
const ActiveCoverSection: React.FC<TProps> = ({
  coverSection = {},
  disable,
  edit,
  riskProfile,
  disabled,
  children,
}) => {
  const isMobile = useUnderBreakpoint("medium");
  const trackEditStarted = useTrackCoverEditStarted();
  const trackRemoved = useTrackCoverRemoved();
  const { icon, title, description, parentCover, product } = coverSection;
  const { id: riskProfileId } = riskProfile;
  const riskData = riskProfile?.riskData;
  const disabledBySubCovers = isCoverDisabledBySubCovers(coverSection, true, riskData);
  const handleEdit = () => {
    edit();
    trackEditStarted({ riskProfileId, cover: coverSection, riskData });
  };
  const handleDisable = () => {
    disable();
    trackRemoved({ riskProfileId, cover: coverSection, riskData });
  };
  return (
    <Block display="flex" flex={1} flexDirection="column">
      <Card
        borderWidth={"0px"}
        display="flex"
        flexDirection="column"
        flex="1"
        marginBottom="1px"
        overrides={{
          Block: {
            props: {
              "data-testid": `activeCoverCard-${toCamelCase(title)}`,
            },
          },
        }}
      >
        <CardBody display="flex" flex={1}>
          <Block display="flex" flex={1}>
            <Block display="flex" flexDirection="column" flex={1} position="relative">
              <Block display="flex" flexDirection="row" flex={1}>
                <Block
                  display="flex"
                  alignItems={isMobile ? "flex-start" : "center"}
                  justifyContent="center"
                  flexDirection={isMobile ? "row" : "column"}
                  flex={9}
                >
                  {icon && !parentCover && (
                    <Block
                      display="block"
                      marginBottom="scale500"
                      marginRight={isMobile ? "scale500" : "initial"}
                    >
                      <CoverSectionIcon active icon={icon} />
                    </Block>
                  )}
                  <HeadingXSmall
                    flex={1}
                    marginTop={0}
                    marginRight="scale400"
                    marginBottom="scale500"
                  >
                    {title}
                  </HeadingXSmall>
                </Block>
                {disable && (
                  <RemoveCoverSectionButton
                    remove={handleDisable}
                    disabled={disabled || disabledBySubCovers}
                  />
                )}
              </Block>

              <Block flex={1}>
                {description && (
                  <ParagraphMedium marginTop={0} color="mono700" flex={1}>
                    {description}
                  </ParagraphMedium>
                )}
              </Block>

              <Block display="flex" flexDirection="column">
                <CoverSummaryFlow
                  coverSection={coverSection}
                  riskProfileId={riskProfileId}
                  disabled={disabled}
                  editable={!!edit}
                />
                <Block>
                  {edit && (
                    <Button
                      onClick={handleEdit}
                      kind={Button.KIND.primary}
                      size={Button.SIZE.mini}
                      disabled={disabled}
                      shape={Button.SHAPE.pill}
                      type="button"
                      overrides={{
                        BaseButton: {
                          style: ({ $theme }) => ({
                            display: "flex",
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginTop: $theme.sizing.scale800,
                            marginBottom: $theme.sizing.scale800,
                            textTransform: "uppercase",
                          }),
                        },
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Block>
              </Block>
            </Block>
          </Block>
        </CardBody>
      </Card>
      {children}
      {!parentCover && product?.provider?.logo && (
        <Block
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          marginTop="scale600"
          height="38px"
        >
          <LabelXSmall color="mono700" $style={{ whiteSpace: "nowrap" }}>
            Underwritten by
          </LabelXSmall>
          <Block maxWidth={["75px", "75px", "90px"]} marginLeft="scale100">
            <img
              src={product.provider.logo}
              alt={product.provider.name}
              width="100%"
              style={{ verticalAlign: "middle" }}
            />
          </Block>
        </Block>
      )}
    </Block>
  );
};

function RemoveCoverSectionButton({ remove, ...props }) {
  return (
    <Block
      display="flex"
      position="absolute"
      right="0"
      alignItems="flex-start"
      justifyContent="flex-end"
    >
      <CloseButton title="Remove" onClick={remove} {...props} />
    </Block>
  );
}

export { ActiveCoverSection };
